// ITEM 9 — THE DAILY IT TICKET SHEET (her word, 2026-08-06). Shared by the server (which files and
// serves) and her dashboard (which renders), so there is ONE answer to what a ticket is, which day it
// belongs to, and what she pastes into CC.
//
// **THE DAY IS PACIFIC AND THAT IS THE WHOLE GROUPING LAW.** Her order: *"grouped BY DAY, and the day
// ends at midnight PACIFIC."* A ticket filed at 23:50 Pacific and one filed ten minutes later are
// different days on her sheet, and both are the SAME UTC day for eight months of the year — so a
// naive `toISOString().slice(0,10)` puts her evening tickets on tomorrow's sheet, every day, and the
// day she marks cleared is not the day she read. DL-1a paid for exactly this one clock over.

/** Her four fields, in her order, which is also the order the copy box prints them. */
export const TICKET_FIELDS = ["email", "date", "issue", "fix"];

/**
 * Where a ticket can come from. Her order named four; ITEM 9 (2026-08-07) added the CONTACT FORM,
 * which is the door most members reach for when something is broken and the only one a member with
 * no account can use at all. VEIL-1 (2026-08-11) adds `veil` — HER OWN HAND, from the group
 * oversight window, where the member never wrote in and does not know the ticket exists.
 *
 * **`veil` CANNOT BE CLAIMED BY A CLIENT**, which is the property the roster below is for: it does
 * not start with `lala-`, so it is not in `LALA_SURFACES` and no chat body may stamp itself with it.
 */
// ITEM 15 — `patrol` is the nightly health check filing its own reds. Like `veil` and `contact` it
// does NOT start with `lala-`, so `LALA_SURFACES` cannot let a chat body claim to be it. It is the
// first source on this roster whose ticket has no member waiting at the other end, which is why the
// caller drops the notice rather than the roster carrying a flag about it.
export const TICKET_SOURCES = ["triage", "lala-drawer", "lala-page", "lala-inbox", "contact", "veil", "patrol"];

/**
 * **WHICH DOORS A LALA TURN MAY CLAIM TO HAVE COME THROUGH, AND WHY THIS IS NOT `TICKET_SOURCES`.**
 *
 * The chat route reads `surface` off the REQUEST BODY and validates it against a roster, so whatever
 * that roster contains is what a client may type about itself. It was `TICKET_SOURCES`, and the two
 * are different questions: `triage` was already claimable (a chat turn stamped as though a
 * classifier had read it), and ITEM 9 would have handed a client `contact` too — a ticket filed from
 * the chat wearing the provenance of the contact form, on the sheet she hands to CC.
 *
 * **A TICKET'S PROVENANCE IS EVIDENCE, AND EVIDENCE A CALLER CAN TYPE IS NOT EVIDENCE** — the law
 * the chat call site states in as many words. So the roster a body is checked against is the CHAT
 * DOORS only, DERIVED by the `lala-` prefix rather than hand-typed beside its superset, so a fifth
 * chat door joins by existing and a non-chat source can never arrive in it.
 */
export const LALA_SURFACES = TICKET_SOURCES.filter((s) => s.startsWith("lala-"));

// ============================================================================================
// ITEM 4b CLAUSE 5 (owner, 2026-08-13) — **THE SHEET HOLDS REAL MEMBER TICKETS ONLY, AND IT IS
// ENFORCED AT THE FILING DOOR RATHER THAN BY SWEEPS.** Her words: *"real member tickets ONLY — no
// patrol rows, no system rows, no test seats — enforced at the filing door, not by sweeps."*
//
// **A SOURCE THAT MAY NO LONGER BE FILED IS NOT A SOURCE THAT NEVER EXISTED, AND COLLAPSING THE TWO
// IS THE ONE THING THAT WOULD BREAK HER SHEET (R18).** `patrol` is retired as a WRITER — A1 deleted
// the nightly patrol's ticket door at its declaration after it filed 81 near-duplicates under her
// own address — and 81 of those rows are still on the table. Taking `patrol` off `TICKET_SOURCES`
// would make every one of them unreadable to `ticketViolations` and to anything that asks what a
// source means. So the roster keeps it and `FILEABLE_SOURCES` is DERIVED (R11/R31): a source joins
// or leaves the WRITE side by name, in one place, and the READ side is untouched.
export const RETIRED_TICKET_SOURCES = ["patrol"];
export const FILEABLE_SOURCES = TICKET_SOURCES.filter((s) => !RETIRED_TICKET_SOURCES.includes(s));

/**
 * **A SEAT THAT CAN NEVER RECEIVE MAIL IS NOT A MEMBER**, so a ticket about one is a fixture on the
 * work order she pastes into CC. R144's `.invalid` law, asked at the FILING DOOR — which is ITEM 12's
 * own ruling: the guard belongs where every caller passes, because the caller that forgets is the one
 * somebody adds next.
 *
 * It is the ADDRESS and never a flag, because this door is reachable by a visitor with no account at
 * all (`userId: null` is legal here) and there is no row to read a flag off.
 */
export const unreachableTicketEmail = (email) => /\.invalid$/i.test(String(email || "").trim());

// ============================================================================================
// ITEM 4b CLAUSE 1 (owner, 2026-08-13) — **EVERY TICKET GETS A NUMBER, AND THERE IS ONLY ONE OF IT.**
// Her words: *"sequential, and visible EVERYWHERE the ticket appears... ONE number aligning all
// surfaces so she can reconcile at a glance."*
//
// >>> **THE NUMBER IS `it_tickets.id` AND NOTHING NEW IS MINTED.** <<< That column is `BIGSERIAL`, so
// it is already sequential, already unique and already what every row on her sheet is keyed by; a
// second numbering beside it would be two answers to *which ticket is this* (R28/R29) — and on a
// work order she reconciles against, two answers is the whole defect her clause names.
//
// >>> **AND MEASURING IT FOUND THAT THE COPY BOX ALREADY CARRIED A SECOND NUMBERING. <<<**
// `ccWorkOrder` printed `1.`, `2.`, `3.` — a POSITION WITHIN ONE DAY. So she reads *"number 3"* off
// the pasted work order, looks at her sheet, and row 3 of a different day is a different ticket
// entirely: the numbers align on the day she copied and on no other. That is not a formatting
// preference, it is the exact failure *"so she can reconcile at a glance"* is written against.
//
// **A ROW WITH NO ID YET IS GIVEN NO NUMBER, RATHER THAN A PLAUSIBLE ONE (R141).** A draft in hand
// before the INSERT has no identity, and printing an invented one on a work order is a false receipt.
// Presence, never truthiness (R53/R136/R137): `Number(true)` is 1 and `Number("")` is 0, so only a
// real positive integer is a ticket number — id `0` is not a row this table can produce.
export const TICKET_REF_PREFIX = "#";
export function ticketRef(id) {
  const n = typeof id === "number" ? id : (typeof id === "string" && /^\d+$/.test(id.trim()) ? Number(id.trim()) : NaN);
  return Number.isSafeInteger(n) && n > 0 ? `${TICKET_REF_PREFIX}${n}` : "";
}

export const PACIFIC = "America/Los_Angeles";

/**
 * The DAY a ticket belongs to, in her zone.
 *
 * `en-CA` because it formats as `YYYY-MM-DD`, which sorts as a string — a day key that needs parsing
 * to be ordered is a day key that will one day be ordered wrongly.
 */
export function pacificDayKey(at, zone = PACIFIC) {
  const d = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(d);
}

/** What a day is called on her sheet. Long form, because she reads it rather than sorts by it. */
export function dayLabel(dayKey, zone = PACIFIC) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dayKey || ""))) return "";
  const [y, m, d] = dayKey.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: zone, weekday: "long", month: "long", day: "numeric", year: "numeric",
  }).format(new Date(Date.UTC(y, m - 1, d, 12)));
}

/**
 * **HER CANNED HEADER, VERBATIM.** It opens the box she pastes into CC, so it is declared once here
 * rather than typed at the surface that renders it — a header composed on the client is a header that
 * drifts from the one she approved (14-B5).
 */
export function ccHeader(dayKey) {
  return `These are the IT issues for ${dayLabel(dayKey) || dayKey}. Here are the customers' information. All of these need to be fixed.`;
}

/**
 * THE COPY BOX: her header, then each ticket one after another.
 *
 * Ready to paste, which means plain text and nothing that only makes sense inside this house — no
 * ids, no internal status words, no machinery. A ticket with no fix written yet says so in words
 * rather than printing an empty line, because a blank field in a work order reads as a mistake.
 */
// **ITEM 4b CLAUSE 1: THE NUMBER IN THE COPY BOX IS THE TICKET'S OWN.** It was `i + 1`, a position
// within one day — see `ticketRef`'s note. A row that somehow reaches here with no id falls back to
// its position and SAYS SO by carrying no `#`, rather than being given a number that means nothing.
export function ccWorkOrder(dayKey, tickets = []) {
  const rows = (Array.isArray(tickets) ? tickets : []).map((t, i) => [
    `${ticketRef(t.id) || `${i + 1}.`} Login email: ${String(t.email || "").trim() || "(no email on file)"}`,
    `   Date: ${String(t.date || dayKey)}`,
    `   Issue: ${String(t.issue || "").trim() || "(not described)"}`,
    `   Fix needed: ${String(t.fix || "").trim() || "(to be determined)"}`,
  ].join("\n"));
  return [ccHeader(dayKey), "", ...rows].join("\n");
}

/** Group a flat list into her days, newest day first, tickets oldest-first inside a day. */
export function ticketsByDay(tickets = []) {
  const days = new Map();
  for (const t of Array.isArray(tickets) ? tickets : []) {
    const key = t.dayKey || pacificDayKey(t.createdAt);
    if (!key) continue;
    if (!days.has(key)) days.set(key, []);
    days.get(key).push(t);
  }
  return [...days.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([dayKey, rows]) => ({
      dayKey,
      label: dayLabel(dayKey),
      tickets: rows,
      // A day is CLEARED when she has marked it. Every ticket carries the stamp, so the day is
      // cleared exactly when they all are — one fact, derived, never a second column on a day table
      // that could disagree with its own rows (R28/R29).
      cleared: rows.length > 0 && rows.every((r) => Boolean(r.clearedAt)),
    }));
}

/**
 * **WHAT THE MEMBER IS TOLD, AND IT NAMES NO MACHINERY.** Her order: every surface that files a
 * ticket tells her warmly, in house language, that a ticket has been placed. Declared once so the
 * four filing surfaces cannot drift, and deliberately says nothing about queues, systems, engineers
 * or tickets-as-machinery beyond the one plain word she asked for.
 */
export const TICKET_PLACED_NOTICE =
  "I have written this down for our team and put it in today's list. Somebody will look at it and get it put right, and you will hear back.";

/** How much of a member's own words her sheet carries. Her copy box is read, so it is not the essay. */
export const TICKET_ISSUE_MAX = 600;

/**
 * ITEM 9 — WHAT THE CONTACT FORM PUTS IN THE `Issue:` LINE.
 *
 * Her form takes a SUBJECT and a MESSAGE and the sheet has one issue field, so the two are joined
 * HERE rather than at the route: the subject is the member's own headline for her problem and
 * dropping it would hand CC a body with no name on it, while the message alone is what the Lala
 * doors already file. The cap is this module's, applied once, so no filing surface can decide its
 * own length for the same sheet.
 */
export function contactTicketIssue({ subject = "", message = "" } = {}) {
  const head = String(subject || "").trim();
  const body = String(message || "").trim();
  return [head, body].filter(Boolean).join(" — ").slice(0, TICKET_ISSUE_MAX);
}

/**
 * **WHAT THE CONTACT FORM ANSWERS, AND THE NOTICE IS CONDITIONAL BECAUSE THE FILING IS.**
 *
 * Two of her three reasons are not IT issues and do not reach that sheet, so a form that always
 * ended with "I have put it in today's list" would tell a member who wrote in about her card that
 * her billing question is on a work order saying it needs to be fixed. `fileMemberTicket` already
 * answers `notice: ""` on a refusal for exactly this reason (R18), and this is the composer that
 * honours it: no notice in, and what comes out is byte-identical to what the form said before ITEM 9
 * existed.
 *
 * The address goes LAST whichever way it composes, because it is the instruction she acts on.
 */
export function contactReply(contactEmail, notice = "") {
  const received = "Your message is with us.";
  const watch = `We answer from ${String(contactEmail || "").trim()}, so keep an eye on that address.`;
  return [received, String(notice || "").trim(), watch].filter(Boolean).join(" ");
}

/**
 * A ticket is only worth filing if it carries what CC needs. Her four fields, and the two that can
 * be missing are DIFFERENT facts (R18): an email we could not resolve makes it unworkable, while a
 * fix nobody has decided yet is normal on the day it is filed.
 */
export function ticketViolations(t = {}) {
  const bad = [];
  if (!String(t.email || "").trim()) bad.push("no-email");
  if (!String(t.issue || "").trim()) bad.push("no-issue");
  // ITEM 4b clause 5: the WRITE side is `FILEABLE_SOURCES`, so a retired writer is refused here while
  // every historical row under it stays readable (R18 — see `RETIRED_TICKET_SOURCES`).
  if (!FILEABLE_SOURCES.includes(String(t.source || ""))) bad.push("unknown-source");
  if (unreachableTicketEmail(t.email)) bad.push("harness-seat");
  return bad;
}
